/**
 * Hook to filter clients based on dashboard filter context
 */

import { useMemo } from "react";
import { ClientType } from "../../../types";
import { useDashboardFilters } from "../contexts/DashboardFilterContext";

/**
 * Calculate age from date of birth
 */
function calculateAge(dateOfBirth: string | undefined | null): number | null {
  if (!dateOfBirth) return null;
  
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    console.error("Error calculating age:", error);
    return null;
  }
}

/**
 * Hook to filter clients based on dashboard filters
 */
export function useFilteredClients(clients: ClientType[]): ClientType[] {
  const { filters } = useDashboardFilters();

  return useMemo(() => {
    let filtered = [...clients];

    // Filter by age range
    if (filters.ageRange) {
      const [minAge, maxAge] = filters.ageRange;
      filtered = filtered.filter((client) => {
        const age = calculateAge(client.dateOfBirth);
        if (age === null) return false;
        if (minAge !== undefined && age < minAge) return false;
        if (maxAge !== undefined && age > maxAge) return false;
        return true;
      });
    }

    // Filter by state/province
    if (filters.stateProvince) {
      filtered = filtered.filter((c) => c.stateProvince === filters.stateProvince);
    }

    // Filter by city
    if (filters.city) {
      filtered = filtered.filter((c) => 
        c.city?.toLowerCase() === filters.city?.toLowerCase()
      );
    }

    // Filter by plan type
    if (filters.planType && filters.planType.length > 0) {
      filtered = filtered.filter((c) => {
        if (c.type === "federal") {
          return filters.planType!.includes(c.retirementPlan || "");
        }
        return filters.planType!.includes(c.type || "");
      });
    }

    // Filter by federal agency
    if (filters.federalAgency) {
      filtered = filtered.filter((c) => 
        c.federalAgency?.toLowerCase() === filters.federalAgency?.toLowerCase()
      );
    }

    // Filter by lifecycle stage
    if (filters.lifecycleStage && filters.lifecycleStage.length > 0) {
      filtered = filtered.filter((c) => 
        c.lifecycleStage && filters.lifecycleStage!.includes(c.lifecycleStage)
      );
    }

    // Filter by status
    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter((c) => 
        c.status && filters.status!.includes(c.status)
      );
    }

    return filtered;
  }, [clients, filters]);
}



